/**
 * Token System Types
 */

// Token transaction types for display
export type TokenTransactionType =
    | 'ad_reward'
    | 'daily_login'
    | 'daily_goal'
    | 'streak_weekly'
    | 'streak_monthly'
    | 'referral_signup'
    | 'referral_subscribe'
    | 'subscriber_monthly'
    | 'event_reward'
    | 'admin_grant'
    | 'purchase'
    | 'unlock_course'
    | 'unlock_dictation'
    | 'unlock_shadowing'
    | 'unlock_quiz'
    | 'unlock_vocabulary'
    | 'ai_analysis'
    | 'ai_question'
    | 'learning_report'
    | 'streak_freeze'
    | 'leaderboard_boost'
    | 'exclusive_content'
    | 'avatar_item'
    | 'refund';

export type TokenTransactionCategory =
    | 'earn_ad'
    | 'earn_engagement'
    | 'earn_referral'
    | 'earn_subscription'
    | 'earn_event'
    | 'earn_purchase'
    | 'earn_admin'
    | 'spend_basic_feature'
    | 'spend_advanced_feature'
    | 'spend_social'
    | 'spend_content'
    | 'spend_cosmetic'
    | 'adjustment';

// User's token statistics
export interface TokenStats {
    balance: number;
    totalEarned: number;
    totalSpent: number;
    loginStreak: number;
    bestLoginStreak: number;
    canClaimDailyLogin: boolean;
    dailyAdTokensRemaining: number;
}

// Token transaction record
export interface TokenTransaction {
    id: string;
    userId: string;
    amount: number;
    balanceAfter: number;
    type: TokenTransactionType;
    category: TokenTransactionCategory;
    description: string | null;
    referenceId: string | null;
    referenceType: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
}

// Token package for purchase
export interface TokenPackage {
    id: string;
    name: string;
    localizedNames: Record<string, string>;
    tokenAmount: number;
    bonusTokens: number;
    priceCents: number;
    discountedPriceCents?: number;  // For subscribers
    subscriberDiscountPercent: number;
    sortOrder: number;
    isActive: boolean;
    isFeatured: boolean;
    badgeText: string | null;
}

// API Response types
export interface TokenStatsResponse {
    success: boolean;
    data: TokenStats;
}

export interface TokenTransactionsResponse {
    success: boolean;
    data: TokenTransaction[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}

export interface EarnTokensResponse {
    success: boolean;
    data: {
        earned: number;
        newBalance: number;
        streak?: number;
        streakBonus?: number;
    };
    message: string;
}

export interface SpendTokensResponse {
    success: boolean;
    data: {
        unlocked?: boolean;
        tokensSpent: number;
        newBalance: number;
    };
    message: string;
}

export interface TokenPackagesResponse {
    success: boolean;
    data: TokenPackage[];
}

// Feature types that can be unlocked with tokens
export type UnlockableFeature = 'course' | 'dictation' | 'shadowing' | 'quiz' | 'vocabulary';
export type AdvancedFeature = 'ai_analysis' | 'ai_question' | 'learning_report' | 'streak_freeze' | 'leaderboard_boost';

// Feature usage status (for free user limit display)
export interface FeatureUsageStatus {
    usedToday: number;
    dailyLimit: number;
    isOverLimit: boolean;
}

export type FeatureStatusMap = Record<UnlockableFeature, FeatureUsageStatus>;

// Ad nonce response
export interface AdPolicy {
    tfcd: 0 | 1; // tag_for_child_directed_treatment (COPPA)
    tfua: 0 | 1; // tag_for_under_age_of_consent (GDPR-K)
    npa: 0 | 1;  // non_personalized_ads
}
export interface AdNonceResponse {
    success: boolean;
    data: { nonce: string; adPolicy?: AdPolicy };
}

// Token costs (for frontend display - should match backend constants)
export const TOKEN_COSTS = {
    // Basic features (free users only)
    UNLOCK_COURSE: 5,
    UNLOCK_DICTATION: 3,
    UNLOCK_SHADOWING: 3,
    UNLOCK_QUIZ: 3,
    UNLOCK_VOCABULARY: 2,

    // Advanced features (all users, subscribers get 50% off)
    AI_PRONUNCIATION_ANALYSIS: 10,
    AI_QUESTION: 8,
    LEARNING_REPORT: 20,
    STREAK_FREEZE: 15,
    LEADERBOARD_BOOST: 10,
} as const;

// Token earning amounts (for frontend display)
export const TOKEN_EARNINGS = {
    AD_REWARD: 3,
    AD_DAILY_LIMIT: 5,
    DAILY_LOGIN: 2,
    DAILY_GOAL_COMPLETE: 5,
    STREAK_7_DAYS: 10,
    STREAK_30_DAYS: 50,
    REFERRAL_SIGNUP: 50,
    REFERRAL_SUBSCRIBE: 200,
    SUBSCRIBER_MONTHLY_BONUS: 100,
} as const;
